import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { CounselingGoal, GoalProgress, Session } from '@/domain/schema/v1';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { SaveIndicator, Select, TextArea, TextInput } from '../../components/fields';
import type { ClientOutlet } from './ClientShell';

const STATUSES = [
  'not_started',
  'exploring',
  'progressing',
  'maintaining',
  'achieved',
  'paused',
  'terminated',
] as const;

/** 상담목표 (MASTER §11): 독립 객체, 회기별 변화 추적 */
export function GoalsScreen() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const [title, setTitle] = useState('');
  const { data, reload } = useLoad(
    async () => ({
      goals: await svc.listGoals(client.id),
      progress: await svc.listGoalProgress({ clientId: client.id }),
      sessions: await svc.listSessions(client.id),
    }),
    [svc, client.id],
  );
  if (!data) return <p className="muted">불러오는 중…</p>;
  return (
    <div className="stack">
      <p className="muted small">
        목표 진행은 회기 진행의 4 검토에서 승인해요. 여기서는 목표를 더하고, 고치고, 회기별 변화를 봐요.
      </p>
      {data.goals.length === 0 && <p className="muted">아직 목표가 없어요. 아래에서 더하세요.</p>}
      <div className="grid-cards goal-grid">
        {data.goals.map((g) => (
          <GoalCard
            key={g.id}
            goal={g}
            progress={data.progress.filter((p) => p.goalId === g.id)}
            sessions={data.sessions}
          />
        ))}
      </div>
      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          void svc.createGoal(client.id, { title: title.trim(), agreedWithClient: true }).then(() => {
            setTitle('');
            reload();
          });
        }}
      >
        <strong>목표 더하기</strong>
        <input
          aria-label="새 목표"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="구체적인 목표 한 문장"
        />
        <button type="submit" className="primary" disabled={!title.trim()}>
          추가
        </button>
      </form>
    </div>
  );
}

function GoalCard({
  goal,
  progress,
  sessions,
}: {
  goal: CounselingGoal;
  progress: GoalProgress[];
  sessions: Session[];
}) {
  const svc = useService();
  const a = useAutosave<CounselingGoal>(goal, (patch, rev) => svc.updateGoal(goal.id, patch, rev));
  const v = a.value!;
  const num = new Map(sessions.map((s) => [s.id, s.sessionNumber]));
  const steps = progress
    .filter((p) => p.approvedStatus)
    .sort((x, y) => (num.get(x.sessionId) ?? 0) - (num.get(y.sessionId) ?? 0));
  return (
    <details className="card">
      <summary>
        <strong>{v.title}</strong> <span className="badge">{goalStatusLabel(v.status)}</span>
        {v.agreedWithClient ? (
          <span className="muted small"> · 내담자와 합의</span>
        ) : (
          <span className="muted small"> · 상담자의 임상적 목표</span>
        )}
      </summary>
      <SaveIndicator status={a.status} error={a.error} />
      <TextInput label="목표" value={v.title} onChange={(x) => x && a.set('title', x)} />
      <TextArea label="설명" value={v.description} onChange={(x) => a.set('description', x || undefined)} />
      <TextInput label="목표 행동" value={v.targetBehavior} onChange={(x) => a.set('targetBehavior', x || undefined)} />
      <TextInput
        label="측정/지표"
        value={v.measurementOrIndicator}
        onChange={(x) => a.set('measurementOrIndicator', x || undefined)}
      />
      <label className="check">
        <input
          type="checkbox"
          checked={!!v.agreedWithClient}
          onChange={(e) => a.set('agreedWithClient', e.target.checked)}
        />{' '}
        내담자와 합의한 목표
      </label>
      <Select
        label="현재 상태 (상담자 결정)"
        value={v.status}
        options={STATUSES.map((s) => ({ value: s, label: goalStatusLabel(s) }))}
        onChange={(x) => a.set('status', x)}
      />
      {steps.length > 0 && (
        <div>
          <strong>회기별 변화</strong>
          <ul>
            {steps.map((p) => (
              <li key={p.id}>
                {num.get(p.sessionId)}회기: {goalStatusLabel(p.approvedStatus!)}
                {p.progressNote ? ` — ${p.progressNote}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </details>
  );
}
